/**
 * §18.3 — the W-4 wizard: the form's own steps, on screen, one at a time.
 *
 * IT RECORDS THE FORM; IT NEVER FILLS IT IN. Every value is what the employee
 * wrote on a line. The worksheets stay with the employee, and nothing here
 * multiplies a count of dependents or works out a deduction: the figures
 * printed on the form change every year, and this stage holds no tax tables
 * (§18.2). So Step 3 is its total, as written.
 *
 * THE EXEMPT BOX IS ASKED FIRST, because it decides which steps exist. An
 * exempt form completes "Steps 1(a), 1(b), and 5. Do not complete any other
 * steps." (Form W-4 (2026), page 2). The wizard skips Steps 2 to 4 for one, and
 * whatever was typed in them before the box was ticked is not sent.
 *
 * THE WORDING IS THE FORM'S, in title case for the labels, because the operator
 * reads each page against the paper.
 *
 * THE SERVER DECIDES. Next is held back only for what cannot be a W-4 at all:
 * a year before the 2020 form, no filing status, an amount that is not one, a
 * form taking effect before it was signed. The server checks everything again,
 * and its sentence is shown on the review page, where the answers still are.
 */

import { useState } from 'react';
import { ApiError, type Employee, type FilingStatus, type RecordW4Body, type W4 } from '../api.js';
import { addressLines, employeeName, filingStatusLabel, maskSsn } from '../lib/labels.js';
import { formatCents, normaliseAmountInput, parseCents } from '../lib/money.js';

const FILING_STATUSES: FilingStatus[] = [
  'single_or_married_separately',
  'married_jointly_or_surviving_spouse',
  'head_of_household',
];

const DATE = /^\d{4}-\d{2}-\d{2}$/;

type Page = 'form' | 'step1' | 'step2' | 'step3' | 'step4' | 'step5' | 'review';

type W4LineValues = Pick<
  W4,
  'exempt' | 'filingStatus' | 'multipleJobs' | 'step3Cents' | 'step4aCents' | 'step4bCents' | 'step4cCents'
>;

/** A W-4's lines, as the form names them. Shared by the review page and the employee's list. */
export function W4Lines({ w4 }: { w4: W4LineValues }) {
  if (w4.exempt) {
    return (
      <dl className="record-facts">
        <dt>Exempt From Withholding</dt>
        <dd>Yes</dd>
      </dl>
    );
  }
  return (
    <dl className="record-facts">
      <dt>Filing Status</dt>
      <dd>{w4.filingStatus ? filingStatusLabel(w4.filingStatus) : '—'}</dd>
      <dt>Step 2(c)</dt>
      <dd>{w4.multipleJobs ? 'Checked' : 'Not Checked'}</dd>
      <dt>Step 3</dt>
      <dd>{formatCents(w4.step3Cents)}</dd>
      <dt>Step 4(a)</dt>
      <dd>{formatCents(w4.step4aCents)}</dd>
      <dt>Step 4(b)</dt>
      <dd>{formatCents(w4.step4bCents)}</dd>
      <dt>Step 4(c)</dt>
      <dd>{formatCents(w4.step4cCents)}</dd>
    </dl>
  );
}

function AmountField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        value={value}
        inputMode="decimal"
        aria-invalid={parseCents(value) === null || undefined}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => onChange(normaliseAmountInput(value))}
      />
    </label>
  );
}

export interface W4WizardProps {
  employee: Employee;
  /** Rejects with the server's refusal, which the review page shows. */
  onSave: (body: RecordW4Body) => Promise<void>;
  onCancel: () => void;
}

export function W4Wizard({ employee, onSave, onCancel }: W4WizardProps) {
  const [page, setPage] = useState<Page>('form');
  const [formYear, setFormYear] = useState(employee.today.slice(0, 4));
  const [exempt, setExempt] = useState(false);
  const [filingStatus, setFilingStatus] = useState<FilingStatus | null>(null);
  const [multipleJobs, setMultipleJobs] = useState(false);
  const [step3, setStep3] = useState('');
  const [step4a, setStep4a] = useState('');
  const [step4b, setStep4b] = useState('');
  const [step4c, setStep4c] = useState('');
  const [signedOn, setSignedOn] = useState('');
  const [effectiveOn, setEffectiveOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pages: Page[] = exempt
    ? ['form', 'step1', 'step5', 'review']
    : ['form', 'step1', 'step2', 'step3', 'step4', 'step5', 'review'];
  const index = pages.indexOf(page);

  const year = Number(formYear);
  const yearOk = /^\d{4}$/.test(formYear.trim()) && year >= 2020;
  const cents = {
    step3: parseCents(step3),
    step4a: parseCents(step4a),
    step4b: parseCents(step4b),
    step4c: parseCents(step4c),
  };
  const bothDates = DATE.test(signedOn) && DATE.test(effectiveOn);
  const tooEarly = bothDates && effectiveOn < signedOn;

  const ready: Record<Page, boolean> = {
    form: yearOk,
    step1: exempt || filingStatus !== null,
    step2: true,
    step3: cents.step3 !== null,
    step4: cents.step4a !== null && cents.step4b !== null && cents.step4c !== null,
    step5: bothDates && !tooEarly,
    review: true,
  };

  /** The answers as the form has them: nothing from Steps 1(c) to 4 on an exempt one. */
  function answers(): RecordW4Body {
    return {
      formYear: year,
      exempt,
      filingStatus: exempt ? null : filingStatus,
      multipleJobs: exempt ? false : multipleJobs,
      step3Cents: exempt ? 0 : (cents.step3 ?? 0),
      step4aCents: exempt ? 0 : (cents.step4a ?? 0),
      step4bCents: exempt ? 0 : (cents.step4b ?? 0),
      step4cCents: exempt ? 0 : (cents.step4c ?? 0),
      signedOn,
      effectiveOn,
    };
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await onSave(answers());
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not record the W-4.');
      setSaving(false);
    }
  }

  const address = addressLines(employee);

  return (
    <div className="new-account w4-wizard" role="group" aria-label="Record a W-4">
      {page === 'form' && (
        <>
          <h3>The Form</h3>
          <label className="field">
            <span>Form Year</span>
            <input value={formYear} inputMode="numeric" onChange={(event) => setFormYear(event.target.value)} />
          </label>
          <p className="muted">The year printed at the top of the form, as in Form W-4 ({employee.today.slice(0, 4)}).</p>
          {!yearOk && formYear.trim() !== '' && (
            <p className="notice notice-warn">
              This records the W-4 in use since 2020. An earlier form counted allowances, which it
              cannot hold.
            </p>
          )}
          <label className="toggle-line">
            <input type="checkbox" checked={exempt} onChange={(event) => setExempt(event.target.checked)} />{' '}
            Exempt From Withholding
          </label>
          <p className="muted">
            Tick this only if the form&apos;s exemption box is checked. An exempt form completes only
            Steps 1(a), 1(b) and 5.
          </p>
        </>
      )}

      {page === 'step1' && (
        <>
          <h3>Step 1: Enter Personal Information</h3>
          <dl className="record-facts">
            <dt>Name</dt>
            <dd>{employeeName(employee)}</dd>
            <dt>Address</dt>
            <dd>{address.length === 0 ? 'Not on file' : address.join(', ')}</dd>
            <dt>SSN</dt>
            <dd>
              {employee.ssn.status === 'on_file'
                ? maskSsn(employee.ssn.lastFour)
                : employee.ssn.status === 'withheld'
                  ? 'On file'
                  : 'Not on file'}
            </dd>
          </dl>
          <p className="muted">
            Steps 1(a) and 1(b) come from the employee&apos;s details. If the form shows a different
            name or address, correct the details first.
          </p>
          {exempt ? (
            <p className="muted">An exempt form has no filing status to record.</p>
          ) : (
            <fieldset className="choice choice-column">
              <legend>Step 1(c) Filing Status</legend>
              {FILING_STATUSES.map((status) => (
                <label key={status}>
                  <input
                    type="radio"
                    name="w4-filing-status"
                    checked={filingStatus === status}
                    onChange={() => setFilingStatus(status)}
                  />{' '}
                  {filingStatusLabel(status)}
                </label>
              ))}
            </fieldset>
          )}
        </>
      )}

      {page === 'step2' && (
        <>
          <h3>Step 2: Multiple Jobs or Spouse Works</h3>
          <label className="toggle-line">
            <input
              type="checkbox"
              checked={multipleJobs}
              onChange={(event) => setMultipleJobs(event.target.checked)}
            />{' '}
            Step 2(c) Box Checked
          </label>
          <p className="muted">
            The box for two jobs in total. A Step 2(b) worksheet result is entered on line 4(c), so it
            is recorded there.
          </p>
        </>
      )}

      {page === 'step3' && (
        <>
          <h3>Step 3: Claim Dependent and Other Credits</h3>
          <AmountField label="Step 3 Total" value={step3} onChange={setStep3} />
          <p className="muted">The total on line 3, as written. A blank line is zero.</p>
        </>
      )}

      {page === 'step4' && (
        <>
          <h3>Step 4: Other Adjustments</h3>
          <AmountField label="4(a) Other Income" value={step4a} onChange={setStep4a} />
          <AmountField label="4(b) Deductions" value={step4b} onChange={setStep4b} />
          <AmountField label="4(c) Extra Withholding" value={step4c} onChange={setStep4c} />
          <p className="muted">As written on the form; 4(c) is for each pay period. A blank line is zero.</p>
        </>
      )}

      {page === 'step5' && (
        <>
          <h3>Step 5: Sign Here</h3>
          <label className="field">
            <span>Date Signed</span>
            <input type="date" value={signedOn} onChange={(event) => setSignedOn(event.target.value)} />
          </label>
          <label className="field">
            <span>Takes Effect</span>
            <input type="date" value={effectiveOn} onChange={(event) => setEffectiveOn(event.target.value)} />
          </label>
          <p className="muted">
            The first day withholding follows this form. Earlier W-4s are kept as they were.
          </p>
          {tooEarly && (
            <p className="notice notice-warn">A W-4 cannot take effect before the date it was signed.</p>
          )}
        </>
      )}

      {page === 'review' && (
        <>
          <h3>Review</h3>
          <W4Lines w4={answers()} />
          <dl className="record-facts">
            <dt>Form Year</dt>
            <dd>{year}</dd>
            <dt>Date Signed</dt>
            <dd>{signedOn}</dd>
            <dt>Takes Effect</dt>
            <dd>{effectiveOn}</dd>
          </dl>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
        </>
      )}

      <div className="row">
        {index > 0 && (
          <button type="button" className="link" onClick={() => setPage(pages[index - 1]!)}>
            Back
          </button>
        )}
        {page === 'review' ? (
          <button type="button" disabled={saving} onClick={() => void save()}>
            Save W-4
          </button>
        ) : (
          <button type="button" disabled={!ready[page]} onClick={() => setPage(pages[index + 1]!)}>
            Next
          </button>
        )}
        <button type="button" className="link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
